
import api from "@/api";
import { User } from "@/api/dtos/User.schema";
import ContainerFrame from "@/components/ContainerFrame.vue";
import EditFrame from "@/components/EditFrame.vue";
import EditActionButtons from "@/components/EditActionButtons.vue";
import { UsersModule } from "@/store/modules/UsersModule";
import { Component, Vue, Watch } from "vue-property-decorator";
import { showToast } from "@/utils/toastService";
import { getAxiosResponse } from "@/utils/errorUtils";

type EditableUser = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  landlinePhone: string | null;
  password: string;
};

@Component({
  components: {
    ContainerFrame,
    EditFrame,
    EditActionButtons,
  },
})
export default class AccountSettingsView extends Vue {
  showPassword1 = false;
  showPassword2 = false;
  passwordConfirm = "";
  isEditing = false;

  localChanges: EditableUser = {
    firstName: "",
    lastName: "",
    email: "",
    phone: null,
    landlinePhone: null,
    password: "",
  };

  // Computed property to determine what data to display
  get displayData(): EditableUser {
    return this.isEditing
      ? this.localChanges
      : {
          firstName: this.localUser.firstName,
          lastName: this.localUser.lastName,
          email: this.localUser.email,
          phone: this.localUser.phone,
          landlinePhone: this.localUser.landlinePhone,
          password: "",
        };
  }

  mounted(): void {
    this.resetLocalChanges();
  }

  resetLocalChanges(): void {
    this.localChanges = {
      firstName: this.localUser.firstName,
      lastName: this.localUser.lastName,
      email: this.localUser.email,
      phone: this.localUser.phone,
      landlinePhone: this.localUser.landlinePhone,
      password: "",
    };
    this.passwordConfirm = "";
  }

  get localUser(): User {
    return UsersModule.localUser!;
  }

  // EditActionButtons hooks
  onEdit(): void {
    this.resetLocalChanges();
    this.isEditing = true;
  }

  async onSave(): Promise<void> {
    try {
      await this.persistChanges();

      this.$nextTick(() => {
        showToast({
          type: "success",
          message: "Profil erfolgreich gespeichert.",
        });
      });
      this.isEditing = false;
    } catch (e) {
      showToast({
        response: getAxiosResponse(e),
        message: "Profil konnte nicht gespeichert werden.",
        type: "error",
      });
    }
  }

  onCancel(): void {
    this.resetLocalChanges();
    this.isEditing = false;
  }

  // Update methods for immediate feedback
  updateFirstName(value: string): void {
    this.localChanges.firstName = value;
  }

  updateLastName(value: string): void {
    this.localChanges.lastName = value;
  }

  updateEmail(value: string): void {
    this.localChanges.email = value;
  }

  updatePhone(value: string): void {
    this.localChanges.phone = value === "" ? null : value;
  }

  updateLandlinePhone(value: string): void {
    this.localChanges.landlinePhone = value === "" ? null : value;
  }

  updatePassword(value: string): void {
    this.localChanges.password = value;
  }

  async persistChanges(): Promise<void> {
    if (this.hasNameChanged()) {
      try {
        await api.UsersApi.updateUserFirstName(
          this.localUser.uuid,
          this.localChanges.firstName
        );
        await api.UsersApi.updateUserLastName(
          this.localUser.uuid,
          this.localChanges.lastName
        );
      } catch (e) {
        showToast({
          response: getAxiosResponse(e),
          message: "Name konnte nicht gespeichert werden.",
          type: "error",
        });
      }
    }

    if (this.hasContactChanged()) {
      try {
        await api.UsersApi.updateUserEmail(
          this.localUser.uuid,
          this.localChanges.email
        );
        await api.UsersApi.updateUserPhone(
          this.localUser.uuid,
          this.localChanges.phone
        );
        await api.UsersApi.updateUserLandlinePhone(
          this.localUser.uuid,
          this.localChanges.landlinePhone
        );
      } catch (e) {
        showToast({
          response: getAxiosResponse(e),
          message: "Kontaktdaten konnten nicht gespeichert werden.",
          type: "error",
        });
      }
    }

    if (this.hasPasswordChanged()) {
      try {
        await api.UsersApi.setUserPassword(
          this.localUser.uuid,
          this.localChanges.password
        );
      } catch (e) {
        showToast({
          response: getAxiosResponse(e),
          message: "Passwort konnte nicht gespeichert werden.",
          type: "error",
        });
      }
    }
  }

  // Change detection methods
  private hasNameChanged(): boolean {
    return (
      this.localUser.firstName !== this.localChanges.firstName ||
      this.localUser.lastName !== this.localChanges.lastName
    );
  }

  private hasContactChanged(): boolean {
    return (
      this.localUser.email !== this.localChanges.email ||
      this.localUser.phone !== this.localChanges.phone ||
      this.localUser.landlinePhone !== this.localChanges.landlinePhone
    );
  }

  private hasPasswordChanged(): boolean {
    return this.localChanges.password !== "";
  }
}
